// askSmart-ui/src/pages/TOSP/components/ConfigurationPanel.tsx
import React, { useEffect, useState } from "react";
import DeleteConfirmModal from "../../../components/DeleteConfirmModal";
import { sameText } from "../validation";

interface ConfigurationPanelProps {
  title: string;
  /** What the panel's list is used for — behind the "?" toggle. */
  description: string;
  /** The committed list — what the rest of the app currently sees. */
  values: string[];
  /** Where the value appears, named in the commit confirmation. */
  usageLabel: string;
  /** How many records currently use a given value. */
  usageCount: (value: string) => number;
  onCommit: (values: string[]) => void | Promise<void>;
}

// One lookup list. Edits are held locally and only reach the rest of the
// application on Commit Changes — the same workflow as the PayFlow payroll
// rules, so nothing half-typed leaks into a dropdown.
export default function ConfigurationPanel({
  title,
  description,
  values,
  usageLabel,
  usageCount,
  onCommit,
}: ConfigurationPanelProps) {
  const [draft, setDraft] = useState<string[]>(values);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [commitOpen, setCommitOpen] = useState(false);
  const [removing, setRemoving] = useState<number | null>(null);

  // Reset whenever the committed list changes underneath — including straight
  // after this panel's own commit.
  useEffect(() => {
    setDraft(values);
    setEditingIndex(null);
    setError(null);
  }, [values]);

  const singular = title.replace(/ Types$/, "").replace(/s$/, "");

  // "+" appends an empty row and opens it for typing — the row itself is the
  // add form, so there's no separate box sitting under the list.
  const addValue = () => {
    setDraft((prev) => [...prev, ""]);
    setEditingIndex(draft.length);
    setError(null);
  };

  const isDirty =
    draft.length !== values.length || draft.some((value, i) => value !== values[i]);

  // Awaited so the confirmation's own spinner runs until the write lands.
  const commit = async () => {
    // Rows added but never typed into are abandoned adds, not entries.
    const next = draft.map((v) => v.trim()).filter(Boolean);
    if (next.some((value, i) => next.some((other, j) => j !== i && sameText(value, other)))) {
      setError("The list has duplicate entries.");
      setCommitOpen(false);
      return;
    }
    await onCommit(next);
    setEditingIndex(null);
    setCommitOpen(false);
    setError(null);
  };

  // A value in use can still be removed — existing records keep what they were
  // saved with — but the count is worth seeing before deciding.
  const inUse = removing === null ? 0 : usageCount(draft[removing]);

  return (
    <section className="tosp-config-panel">
      <div className="tosp-config-header">
        <h3 className="tosp-config-title">{title}</h3>
        <button
          type="button"
          className="tosp-help-toggle"
          aria-expanded={helpOpen}
          title="What's this?"
          onClick={() => setHelpOpen((open) => !open)}
        >
          ?
        </button>
        <button
          type="button"
          className="tosp-panel-add"
          title={`Add a ${singular.toLowerCase()}`}
          onClick={addValue}
        >
          +
        </button>
      </div>

      {helpOpen && <p className="tosp-config-note">{description}</p>}

      <div className="tosp-config-list">
        {draft.length === 0 && <p className="tosp-config-empty">No values yet.</p>}
        {draft.map((value, index) => (
          <div key={index} className="tosp-config-row">
            {editingIndex === index ? (
              <input
                className="manage-input"
                autoComplete="off"
                aria-label={`${title} value ${index + 1}`}
                value={value}
                autoFocus
                onChange={(e) =>
                  setDraft((prev) => prev.map((v, i) => (i === index ? e.target.value : v)))
                }
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  setEditingIndex(null);
                }}
              />
            ) : (
              <span className="tosp-config-value">{value}</span>
            )}
            <button
              type="button"
              className={`tosp-btn-icon tosp-btn-icon-sm ${
                editingIndex === index ? "tosp-btn-confirm" : "tosp-btn-edit"
              }`}
              title={editingIndex === index ? "Done" : "Rename"}
              onClick={() => setEditingIndex(editingIndex === index ? null : index)}
            >
              {editingIndex === index ? "✓" : "✎"}
            </button>
            <button
              type="button"
              className="tosp-btn-icon tosp-btn-delete tosp-btn-icon-sm"
              title="Delete"
              onClick={() => setRemoving(index)}
            >
              🗑
            </button>
          </div>
        ))}
      </div>

      {error && <p className="tosp-field-error">{error}</p>}

      <div className="tosp-config-footer">
        <button
          type="button"
          className="tosp-commit-btn"
          disabled={!isDirty}
          title={isDirty ? undefined : "No changes to commit"}
          onClick={() => setCommitOpen(true)}
        >
          Commit
        </button>
      </div>

      <DeleteConfirmModal
        isOpen={commitOpen}
        title={`Commit ${title}`}
        message={`This updates ${title} everywhere in TOSP — ${usageLabel} will offer this list from now on.`}
        confirmLabel="Continue"
        confirmColor="#1faa59"
        onClose={() => setCommitOpen(false)}
        onConfirm={commit}
      />

      <DeleteConfirmModal
        isOpen={removing !== null}
        title={`Delete ${singular}`}
        message={
          removing === null
            ? ""
            : `Remove "${draft[removing]}" from the list?${
                inUse > 0
                  ? ` ${inUse} record${inUse === 1 ? "" : "s"} currently use it and will keep the value until edited.`
                  : ""
              } The change takes effect when you commit.`
        }
        onClose={() => setRemoving(null)}
        onConfirm={() => {
          setDraft((prev) => prev.filter((_, i) => i !== removing));
          setEditingIndex(null);
          setRemoving(null);
        }}
      />
    </section>
  );
}
